// src/utils/calculations.js
// Единый модуль для всех расчетов и констант

// ========================
// КОНСТАНТЫ
// ========================

// Даты для исключения (праздничные дни)
export const EXCLUDED_DATES = [
  '2025-11-03', '2025-11-04', '2025-11-05',
  '2026-01-01', '2026-01-02', '2026-01-03', '2026-01-04', '2026-01-05',
  '2026-01-06', '2026-01-07', '2026-01-08', '2026-01-09',
  '2026-03-30', '2026-03-31', '2026-04-01', '2026-04-02', '2026-04-03',
  '2026-04-04', '2026-04-05'
];

// Пороговые значения
export const THRESHOLDS = {
  COMPLIANCE_CRITICAL: 95,      // Низкое соответствие меню (<70%)
  TIMELINESS_GOOD: 100,          // Хорошая своевременность (=100%)
  PUBLICATION_PERCENT: 0.8,      // Порог публикации (80% от ожидаемых дней)
  COMPLIANCE_SCHOOL_TABLE: 95,   // Порог для таблицы школ (>=95% - успех)
  COMPLIANCE_CITY_VIEW: 95       // Порог для CityView (<85% - проблема)
};

// Отсечка для дат документов
export const CUTOFF_DATES = {
  TM_RK: '01.08.2025',  // Типовое меню и родительский контроль
  KP: '01.01.2026'       // Календарь питания
};

// ========================
// ФУНКЦИИ ДЛЯ РАБОТЫ С ДАТАМИ
// ========================

export const isDateOutdated = (dateString, cutoffDate = CUTOFF_DATES.TM_RK) => {
  if (!dateString) return true;
  const cutoff = new Date(cutoffDate.split('.').reverse().join('-'));
  const date = new Date(dateString.split('.').reverse().join('-'));
  return date < cutoff;
};

export const isDateOutdatedcp = (dateString) => {
  return isDateOutdated(dateString, CUTOFF_DATES.KP);
};

export const isDateOutdatedTmRk = (dateString) => {
  return isDateOutdated(dateString, CUTOFF_DATES.TM_RK);
};

// ========================
// ФУНКЦИИ ДЛЯ ПУБЛИКАЦИИ МЕНЮ
// ========================

export const calculateExpectedPublicationDays = (month, year, considerCurrentDate = false, excludedDates = EXCLUDED_DATES) => {
  const firstDay = new Date(year, month - 1, 1);
  let lastDay;
  
  if (considerCurrentDate) {
    const now = new Date();
    const selectedMonth = new Date(year, month - 1, 1);
    const isCurrentMonth = now.getMonth() === selectedMonth.getMonth() && now.getFullYear() === selectedMonth.getFullYear();
    
    if (isCurrentMonth) {
      lastDay = new Date(now);
    } else {
      lastDay = new Date(year, month, 0);
    }
  } else {
    lastDay = new Date(year, month, 0);
  }
  
  let workingDays = 0;
  let currentDate = new Date(firstDay);
  
  while (currentDate <= lastDay) {
    const dayOfWeek = currentDate.getDay();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    const currentDateStr = `${year}-${month}-${day}`;
    
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isExcluded = excludedDates.includes(currentDateStr);
    
    if (!isWeekend && !isExcluded) {
      workingDays++;
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return workingDays;
};

export const calculatePublicationProblems = (data, month, year) => {
  const publicationBySchool = {};
  
  if (data.publication) {
    data.publication.forEach(item => {
      if (!publicationBySchool[item.Код]) {
        publicationBySchool[item.Код] = new Set();
      }
      publicationBySchool[item.Код].add(item.Дата);
    });
  }

  const expectedPublicationDays = calculateExpectedPublicationDays(month, year, true);
  const goodThreshold = Math.max(1, Math.floor(expectedPublicationDays * THRESHOLDS.PUBLICATION_PERCENT));

  const schoolsWithPublicationIssues = (data.dates || []).filter(school => {
    const publicationDays = publicationBySchool[school.Код] ? publicationBySchool[school.Код].size : 0;
    return publicationDays < goodThreshold;
  });

  return schoolsWithPublicationIssues;
};

export const hasPublicationProblems = (school, data, month, year) => {
  const publicationProblems = calculatePublicationProblems(data, month, year);
  return publicationProblems.some(s => s.Код === school.Код);
};

// ========================
// ФУНКЦИИ ДЛЯ СВОЕВРЕМЕННОСТИ
// ========================

export const getSchoolTimeliness = (school, timelinessData) => {
  if (!timelinessData || timelinessData.length === 0) return null;
  const timelinessRecord = timelinessData.find(item => item.Пищеблок === school.Наименование);
  return timelinessRecord ? timelinessRecord.ПроцентСвоевременности : null;
};

export const hasTimelinessProblems = (school, timelinessData, threshold = THRESHOLDS.TIMELINESS_GOOD) => {
  const percentage = getSchoolTimeliness(school, timelinessData);
  if (percentage === null) return true;
  return percentage < threshold;
};

export const getTimelinessStatus = (school, timelinessData, threshold = THRESHOLDS.TIMELINESS_GOOD) => {
  const percentage = getSchoolTimeliness(school, timelinessData);
  
  if (percentage === null) {
    return { status: 'error', text: 'Нет данных', symbol: '❌' };
  }
  
  if (percentage >= threshold) {
    return { status: 'success', text: `Отлично (${percentage}%)`, symbol: '✓' };
  } else {
    return { status: 'error', text: `Нарушение (${percentage}%)`, symbol: '❌' };
  }
};

// ========================
// ФУНКЦИИ ДЛЯ СООТВЕТСТВИЯ МЕНЮ
// ========================

export const getSchoolCompliance = (school, complianceData) => {
  const schoolCompliance = complianceData.filter(item => 
    item.ПищеблокНаименование && item.ПищеблокНаименование.includes(school.Наименование)
  );
  
  if (schoolCompliance.length === 0) return 100;
  
  return schoolCompliance.reduce((sum, item) => sum + (item.ПроцентСоблюдения || 0), 0) / schoolCompliance.length;
};

export const hasComplianceProblems = (school, complianceData, threshold = THRESHOLDS.COMPLIANCE_CRITICAL) => {
  const avgCompliance = getSchoolCompliance(school, complianceData);
  return avgCompliance < threshold;
};

// ========================
// ФУНКЦИИ ДЛЯ ОШИБОК В МЕНЮ
// ========================

export const hasErrorsInMenu = (school, errorsData) => {
  return errorsData.some(error => 
    error.Пищеблок && error.Пищеблок.includes(school.Наименование)
  );
};

export const getErrorsCount = (school, errorsData) => {
  return errorsData.filter(error => 
    error.Пищеблок && error.Пищеблок.includes(school.Наименование)
  ).length;
};

// ========================
// ЕДИНАЯ ФУНКЦИЯ ПРОВЕРКИ ПРОБЛЕМ ШКОЛЫ
// ========================

export const hasSchoolProblems = (school, allData, filters) => {
  const { errors, compliance, timeliness } = allData;
  const { month, year } = filters;
  
  if (isDateOutdatedTmRk(school.ДатаТМ)) return true;
  if (isDateOutdatedcp(school.ДатаКП)) return true;
  if (isDateOutdatedTmRk(school.ДатаРК)) return true;
  
  if (hasErrorsInMenu(school, errors)) return true;
  if (hasComplianceProblems(school, compliance, THRESHOLDS.COMPLIANCE_CRITICAL)) return true;
  if (hasPublicationProblems(school, allData, month, year)) return true;
  if (hasTimelinessProblems(school, timeliness, THRESHOLDS.TIMELINESS_GOOD)) return true;
  
  return false;
};

export const countSchoolProblems = (school, allData, filters) => {
  const { errors, compliance, timeliness } = allData;
  const { month, year } = filters;
  let count = 0;
  
  if (isDateOutdatedTmRk(school.ДатаТМ)) count++;
  if (isDateOutdatedcp(school.ДатаКП)) count++;
  if (isDateOutdatedTmRk(school.ДатаРК)) count++;
  
  count += getErrorsCount(school, errors);
  
  if (hasComplianceProblems(school, compliance, THRESHOLDS.COMPLIANCE_CRITICAL)) count++;
  if (hasPublicationProblems(school, allData, month, year)) count++;
  if (hasTimelinessProblems(school, timeliness, THRESHOLDS.TIMELINESS_GOOD)) count++;
  
  return count;
};

export const getSchoolProblemsList = (school, allData, filters) => {
  const { errors, compliance, timeliness } = allData;
  const { month, year } = filters;
  const problems = [];
  
  if (isDateOutdatedTmRk(school.ДатаТМ)) {
    problems.push(`Типовое меню: устарело (${school.ДатаТМ || 'нет даты'})`);
  }
  
  if (isDateOutdatedcp(school.ДатаКП)) {
    problems.push(`Календарь питания: устарел (${school.ДатаКП || 'нет даты'})`);
  }
  
  if (isDateOutdatedTmRk(school.ДатаРК)) {
    problems.push(`Родительский контроль: устарел (${school.ДатаРК || 'нет даты'})`);
  }
  
  const schoolErrors = errors.filter(error => error.Пищеблок && error.Пищеблок.includes(school.Наименование));
  if (schoolErrors.length > 0) {
    problems.push(`Ошибки в меню: ${schoolErrors.length} шт.`);
  }
  
  if (hasComplianceProblems(school, compliance, THRESHOLDS.COMPLIANCE_CRITICAL)) {
    const avgCompliance = getSchoolCompliance(school, compliance);
    problems.push(`Низкое соответствие меню: ${avgCompliance.toFixed(1)}%`);
  }
  
  if (hasPublicationProblems(school, allData, month, year)) {
    problems.push(`Публикация меню: нерегулярная`);
  }
  
  const timelinessPercentage = getSchoolTimeliness(school, timeliness);
  if (timelinessPercentage === null) {
    problems.push(`Своевременность: нет данных`);
  } else if (timelinessPercentage < THRESHOLDS.TIMELINESS_GOOD) {
    problems.push(`Своевременность: ${timelinessPercentage}% (нужно 100%)`);
  }
  
  return problems;
};

// ========================
// ФУНКЦИИ ДЛЯ РАЙОНОВ
// ========================

export const getDistrictProblemSchoolsCount = (districtSchools, allData, filters) => {
  let problemSchoolsCount = 0;
  
  districtSchools.forEach(school => {
    if (hasSchoolProblems(school, allData, filters)) {
      problemSchoolsCount++;
    }
  });
  
  return problemSchoolsCount;
};

export const getDistrictProblemsByType = (districtSchools, allData, filters) => {
  const { errors, compliance, publication, timeliness } = allData;
  const { month, year } = filters;
  
  return {
    'Устаревшее ТМ': districtSchools.filter(school => isDateOutdatedTmRk(school.ДатаТМ)).length,
    'Устаревшее КП': districtSchools.filter(school => isDateOutdatedcp(school.ДатаКП)).length,
    'Устаревшее РК': districtSchools.filter(school => isDateOutdatedTmRk(school.ДатаРК)).length,
    'Ошибки в меню': [...new Set(
      errors
        .filter(error => districtSchools.some(school => error.Пищеблок && error.Пищеблок.includes(school.Наименование)))
        .map(error => error.Пищеблок)
    )].length,
    'Низкое соответствие': districtSchools.filter(school => 
      hasComplianceProblems(school, compliance, THRESHOLDS.COMPLIANCE_CRITICAL)
    ).length,
    'Проблемы публикации': calculatePublicationProblems(allData, month, year)
      .filter(school => districtSchools.some(ds => ds.Код === school.Код)).length,
    'Низкая своевременность': districtSchools.filter(school => 
      hasTimelinessProblems(school, timeliness, THRESHOLDS.TIMELINESS_GOOD)
    ).length
  };
};

// ========================
// ФУНКЦИИ ДЛЯ ИЗВЛЕЧЕНИЯ НОМЕРА ШКОЛЫ
// ========================

export const extractSchoolNumber = (schoolName) => {
  const match = schoolName.match(/№\s*(\d+)/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return null;
};

export const getSchoolNumber = (schoolName) => {
  const numberMatch = schoolName.match(/№\s*(\d+)/);
  if (numberMatch && numberMatch[1]) {
    return numberMatch[1];
  }
  return schoolName.substring(0, 6) + (schoolName.length > 6 ? '...' : '');
};

// ========================
// ФУНКЦИИ ДЛЯ СТАТИСТИКИ ГОРОДА
// ========================

export const getCityStatistics = (allData, filters) => {
  const { dates, errors, compliance, timeliness } = allData;
  const { month, year } = filters;
  const totalSchools = dates.length;
  const districts = [...new Set(dates.map(item => item.РайонНаименование))].filter(Boolean);
  
  const problemsByType = {
    'Устаревшее ТМ': dates.filter(item => isDateOutdatedTmRk(item.ДатаТМ)).length,
    'Устаревшее КП': dates.filter(item => isDateOutdatedcp(item.ДатаКП)).length,
    'Устаревшее РК': dates.filter(item => isDateOutdatedTmRk(item.ДатаРК)).length,
    'Ошибки в меню': errors.length,
    'Низкое соответствие': compliance.filter(item => item.ПроцентСоблюдения < THRESHOLDS.COMPLIANCE_CITY_VIEW).length,
    'Проблемы публикации': calculatePublicationProblems(allData, month, year).length,
    'Низкая своевременность': dates.filter(school => hasTimelinessProblems(school, timeliness, THRESHOLDS.TIMELINESS_GOOD)).length,
  };
  
  const totalProblems = Object.values(problemsByType).reduce((sum, count) => sum + count, 0);
  const problemSchools = dates.filter(school => hasSchoolProblems(school, allData, filters)).length;
  
  return {
    totalDistricts: districts.length,
    totalSchools,
    problemSchools,
    totalProblems,
    problemsByType
  };
};

export const getDistrictData = (allData, filters) => {
  const { dates } = allData;
  const districts = [...new Set(dates.map(item => item.РайонНаименование))].filter(Boolean);
  
  return districts.map(district => {
    const districtSchools = dates.filter(school => school.РайонНаименование === district);
    const totalSchools = districtSchools.length;
    
    const problemSchools = districtSchools.filter(school => 
      hasSchoolProblems(school, allData, filters)
    ).length;
    
    const problemPercentage = totalSchools > 0 ? (problemSchools / totalSchools * 100).toFixed(1) : 0;
    const problemsByType = getDistrictProblemsByType(districtSchools, allData, filters);
    
    return {
      district,
      totalSchools,
      problemSchools,
      problemPercentage: parseFloat(problemPercentage),
      problemsByType
    };
  }).sort((a, b) => b.problemPercentage - a.problemPercentage);
};